type RealtimeEventType =
  | "machine.changed"
  | "location.changed"
  | "invite.changed"
  | "expense.changed"
  | "task.changed";

type RealtimeEvent = {
  type?: string;
  machineId?: string;
  locationId?: string;
  inviteId?: string;
  taskId?: string;
  expenseId?: string;
  scheduleId?: string;
};

const suppressedUntil = new Map<string, number>();
const DEFAULT_SUPPRESS_MS = 2000;

const getSuppressionKey = (
  type: RealtimeEventType,
  entityId?: string | null,
) => (entityId ? `${type}:${entityId}` : type);

export function suppressRealtimeEvent(
  type: RealtimeEventType,
  entityIdOrDurationMs?: string | number,
  durationMs = DEFAULT_SUPPRESS_MS
) {
  const entityId =
    typeof entityIdOrDurationMs === "string" ? entityIdOrDurationMs : undefined;
  const suppressMs =
    typeof entityIdOrDurationMs === "number" ? entityIdOrDurationMs : durationMs;
  suppressedUntil.set(getSuppressionKey(type, entityId), Date.now() + suppressMs);
}

const getEventSuppressionKey = (event?: RealtimeEvent | null) => {
  if (!event?.type) return null;
  if (event.type === "machine.changed") {
    return getSuppressionKey("machine.changed", event.machineId);
  }
  if (event.type === "location.changed") {
    return getSuppressionKey("location.changed", event.locationId);
  }
  if (event.type === "invite.changed") {
    return getSuppressionKey("invite.changed", event.inviteId);
  }
  if (event.type === "task.changed") {
    return getSuppressionKey("task.changed", event.taskId);
  }
  if (event.type === "expense.changed") {
    return getSuppressionKey(
      "expense.changed",
      event.expenseId || event.scheduleId,
    );
  }
  return null;
};

export function shouldSuppressRealtimeEvent(event?: RealtimeEvent | null) {
  const key = getEventSuppressionKey(event);
  if (!key) return false;
  const until = suppressedUntil.get(key) || suppressedUntil.get(event?.type || "");
  if (!until) return false;
  if (until > Date.now()) return true;
  suppressedUntil.delete(key);
  suppressedUntil.delete(event?.type || "");
  return false;
}
